import * as React from 'react'

import { Redo2Icon, Undo2Icon } from 'lucide-react'
import { isMacOs } from 'react-device-detect'

import { LocationEditIcon } from '~/client/src/shared/icons'
import Localization from '~/client/src/shared/localization/LocalizationManager'

import MarkupGesturesLegend from './MarkupGesturesLegend'
import MarkupShortcutsLegend, { IShortcutGroup } from './MarkupShortcutsLegend'
import MarkupToolButton from './MarkupToolButton'
import { MAIN_TOOLS, MarkupTool } from './markupTools'
import MobileMarkupToolButton from './MobileMarkupToolButton'

// macOS users expect the ⌘/⇧ glyphs; others get spelled-out Ctrl.
const UNDO_SHORTCUT = isMacOs ? '⌘Z' : 'Ctrl+Z'
const REDO_SHORTCUT = isMacOs ? '⇧⌘Z' : 'Ctrl+Shift+Z'

interface IProps {
  activeTool: MarkupTool
  canUndo: boolean
  canRedo: boolean
  legendOpen: boolean
  showLegendHint: boolean
  onSelectTool: (tool: MarkupTool) => void
  onUndo: () => void
  onRedo: () => void
  onLegendOpenChange: (open: boolean) => void
  // Touch: drop the keyboard-shortcut affordances (Kbd hints + the shortcut legend).
  isMobileEnv?: boolean
  // Leftmost "Edit Precise Location" pin tool (shown only when the parent allows
  // editing the location); toggles the parent's pin-edit sub-mode.
  showLocationTool?: boolean
  isLocationEditActive?: boolean
  onEditPreciseLocation?: () => void
  // Host-specific shortcut groups prepended to the legend.
  legendExtraGroups?: IShortcutGroup[]
  hideLegend?: boolean
}

const CARD =
  'tw:flex tw:items-center tw:gap-1 tw:rounded-lg tw:bg-popover tw:p-1 tw:shadow-lg'

const MarkupToolbar: React.FC<IProps> = ({
  activeTool,
  canUndo,
  canRedo,
  legendOpen,
  showLegendHint,
  onSelectTool,
  onUndo,
  onRedo,
  onLegendOpenChange,
  isMobileEnv,
  showLocationTool,
  isLocationEditActive,
  onEditPreciseLocation,
  legendExtraGroups,
  hideLegend,
}) => {
  const toolbarRef = React.useRef<HTMLDivElement>(null)
  // Touch build swaps in the wrapper that survives the swallowed first-tap click after a
  // keyboard dismissal; desktop uses the plain button.
  const ToolButton = isMobileEnv ? MobileMarkupToolButton : MarkupToolButton
  return (
    <div
      ref={toolbarRef}
      role="toolbar"
      aria-label={Localization.translator.markupTools}
      className="tw:flex tw:items-start tw:gap-2"
    >
      {showLocationTool && onEditPreciseLocation && (
        <div className={CARD}>
          <ToolButton
            icon={LocationEditIcon}
            label={Localization.translator.movePinPosition}
            shortcut={isMobileEnv ? undefined : 'M'}
            isActive={!!isLocationEditActive}
            onClick={onEditPreciseLocation}
          />
        </div>
      )}
      <div className={CARD}>
        {MAIN_TOOLS.map(({ value, icon, label, shortcut, disabled }) => (
          <ToolButton
            key={value}
            icon={icon}
            label={label}
            shortcut={isMobileEnv ? undefined : shortcut}
            isActive={!isLocationEditActive && activeTool === value}
            disabled={disabled}
            // Click the active tool to toggle back to Select (no-Esc bail-out).
            onClick={() =>
              onSelectTool(activeTool === value ? 'select' : value)
            }
          />
        ))}
      </div>
      <div className={CARD}>
        <ToolButton
          icon={Undo2Icon}
          label={Localization.translator.undo}
          shortcut={isMobileEnv ? undefined : UNDO_SHORTCUT}
          disabled={!canUndo}
          onClick={onUndo}
        />
        <ToolButton
          icon={Redo2Icon}
          label={Localization.translator.redo}
          shortcut={isMobileEnv ? undefined : REDO_SHORTCUT}
          disabled={!canRedo}
          onClick={onRedo}
        />
      </div>
      {!hideLegend && (
        <div className={CARD}>
          {isMobileEnv ? (
            <MarkupGesturesLegend
              open={legendOpen}
              onOpenChange={onLegendOpenChange}
              showLocationTool={showLocationTool}
            />
          ) : (
            <MarkupShortcutsLegend
              open={legendOpen}
              showHint={showLegendHint}
              onOpenChange={onLegendOpenChange}
              showLocationTool={showLocationTool}
              extraGroups={legendExtraGroups}
              anchorRef={toolbarRef}
            />
          )}
        </div>
      )}
    </div>
  )
}

export default MarkupToolbar
